import { Link } from 'react-router-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

/**
 * MKS-388 — the persistent `/time` banner for a Claude seat holder whose Monka
 * is not connected. Shown from the moment the page loads, not only after a
 * save comes back 403, because the point is that the person never types an
 * entry they are not allowed to save.
 *
 * The copy is the ticket's, verbatim, and is deliberately duplicated from the
 * backend's `MONKA_CONNECTION_REQUIRED_MESSAGE` / `_STEPS`
 * (`backend/src/services/claudeSeatService.ts`), which is what the API returns
 * in `message` + `details` so the CLI prints the same guidance. The two
 * projects share no constants module, so there is nothing to import.
 */
export function MonkaConnectionBanner() {
  return (
    <div
      role="alert"
      className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
    >
      <div className="flex gap-3">
        <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 text-amber-600" aria-hidden />
        <div className="space-y-2">
          <p>
            <strong className="font-semibold">Connect Monka to log time.</strong> You have a Claude
            seat, and every seat holder needs Monka connected so their Claude usage is attributed
            to them. Logging time is paused until you connect it.
          </p>
          <div>
            <p className="font-semibold">How to connect (2 minutes):</p>
            <ol className="list-decimal list-inside space-y-1 mt-1">
              <li>
                In Slack, open a DM with <strong className="font-semibold">Monka</strong> and write{' '}
                <code className="px-1 py-0.5 rounded bg-amber-100 font-mono text-xs">connect</code>.
                Monka replies with your personal connect link.
              </li>
              <li>
                In a terminal, run{' '}
                <code className="px-1 py-0.5 rounded bg-amber-100 font-mono text-xs">
                  claude setup-token
                </code>{' '}
                and copy the token it prints.
              </li>
              <li>
                Open the link from step 1 and paste the token. That&apos;s it: time logging works
                again immediately.
              </li>
            </ol>
          </div>
          <p>
            Questions? Ask in #monka or contact Jiří Fabián. You can also check your connection on
            your{' '}
            <Link to="/profile" className="font-semibold underline hover:text-amber-700">
              profile page
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  );
}
